/*
 * THE FLYING BEANS — INTERACTIVE ORIGIN HERO CONTROLLER (REBUILT)
 * Guarantee: Zero Ghosting, Strict autoAlpha Visibility, Integrated Navigation
 * Platform: Shopify 2.0
 */

(function () {
  'use strict';

  window.TFB = window.TFB || {};

  /**
   * Deterministic Script Loader
   */
  window.TFB.loadScript = function (src) {
    return new Promise(function (resolve, reject) {
      const existing = document.querySelector('script[src="' + src + '"]');
      if (existing) {
        if (existing.dataset.loaded === 'true') return resolve();
        existing.addEventListener('load', function () { resolve(); });
        existing.addEventListener('error', reject);
        return;
      }

      const script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.dataset.loaded = 'false';
      script.onload = function () {
        script.dataset.loaded = 'true';
        resolve();
      };
      script.onerror = reject;
      document.head.appendChild(script);
    });
  };

  /**
   * Singleton GSAP Loader (GSAP Core -> ScrollTrigger & ScrollToPlugin)
   */
  window.TFB.loadGSAP = function (gsapUrl, stUrl, scrollToUrl) {
    if (window.TFB._gsapPromise) return window.TFB._gsapPromise;

    window.TFB._gsapPromise = new Promise(function (resolve, reject) {
      if (window.gsap && window.ScrollTrigger && window.ScrollToPlugin) {
        return resolve({
          gsap: window.gsap,
          ScrollTrigger: window.ScrollTrigger,
          ScrollToPlugin: window.ScrollToPlugin
        });
      }

      window.TFB.loadScript(gsapUrl)
        .then(function () {
          return Promise.all([
            window.TFB.loadScript(stUrl),
            window.TFB.loadScript(scrollToUrl)
          ]);
        })
        .then(function () {
          if (window.gsap && window.ScrollTrigger && window.ScrollToPlugin) {
            window.gsap.registerPlugin(window.ScrollTrigger, window.ScrollToPlugin);
            resolve({
              gsap: window.gsap,
              ScrollTrigger: window.ScrollTrigger,
              ScrollToPlugin: window.ScrollToPlugin
            });
          } else {
            reject(new Error('GSAP plugins failed to register cleanly.'));
          }
        })
        .catch(function (err) {
          window.TFB._gsapPromise = null;
          reject(err);
        });
    });

    return window.TFB._gsapPromise;
  };

  /**
   * Initialize Origin Hero
   */
  function initOriginHero(sectionEl) {
    if (!sectionEl || sectionEl._tfbInitialized) return;

    const gsapUrl = sectionEl.dataset.gsapUrl;
    const stUrl = sectionEl.dataset.scrolltriggerUrl;
    const scrollToUrl = sectionEl.dataset.scrolltoUrl;
    const enableSnap = sectionEl.dataset.enableSnap !== 'false';
    const originCount = parseInt(sectionEl.dataset.originCount, 10) || 1;

    window.TFB.loadGSAP(gsapUrl, stUrl, scrollToUrl)
      .then(function (bundle) {
        const gsap = bundle.gsap;
        const ScrollTrigger = bundle.ScrollTrigger;

        const viewport = sectionEl.querySelector('.tfb-hero__viewport');
        const bgLayers = sectionEl.querySelectorAll('.tfb-hero__bg-layer');
        const wordmarks = sectionEl.querySelectorAll('.tfb-hero__wordmark-wrap');
        const artworks = sectionEl.querySelectorAll('.tfb-hero__artwork-wrap');
        const products = sectionEl.querySelectorAll('.tfb-hero__product-motion-wrap');
        const metaItems = sectionEl.querySelectorAll('.tfb-hero__meta-item');
        const counterCurrent = sectionEl.querySelector('.tfb-hero__counter-current');
        const prevBtn = sectionEl.querySelector('.tfb-hero__nav-prev');
        const nextBtn = sectionEl.querySelector('.tfb-hero__nav-next');
        const navBar = sectionEl.querySelector('.tfb-hero__nav-bar');
        const mobileToggle = sectionEl.querySelector('.tfb-hero__mobile-toggle');
        const mobileDrawer = sectionEl.querySelector('.tfb-hero__mobile-drawer');

        // Clean existing instances
        destroyOriginHero(sectionEl);

        // Mobile drawer toggle & close
        if (mobileToggle && mobileDrawer) {
          const closeBtn = mobileDrawer.querySelector('.tfb-mobile-drawer__close');

          function closeHeroDrawer() {
            mobileDrawer.classList.remove('is-open');
            mobileToggle.classList.remove('is-open');
            mobileToggle.setAttribute('aria-expanded', 'false');
            mobileDrawer.setAttribute('aria-hidden', 'true');
          }

          mobileToggle.addEventListener('click', function () {
            const isOpen = mobileDrawer.classList.toggle('is-open');
            mobileToggle.classList.toggle('is-open', isOpen);
            mobileToggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
            mobileDrawer.setAttribute('aria-hidden', isOpen ? 'false' : 'true');
          });

          if (closeBtn) {
            closeBtn.addEventListener('click', function (e) {
              e.preventDefault();
              closeHeroDrawer();
            });
          }

          const heroMobileDropdownBtn = mobileDrawer.querySelector('.tfb-mobile-dropdown-btn');
          const heroMobileDropdownContent = mobileDrawer.querySelector('.tfb-mobile-dropdown-content');
          if (heroMobileDropdownBtn && heroMobileDropdownContent) {
            heroMobileDropdownBtn.addEventListener('click', function(e) {
              e.preventDefault();
              const isExpanded = heroMobileDropdownBtn.classList.toggle('is-expanded');
              heroMobileDropdownContent.classList.toggle('is-open', isExpanded);
              heroMobileDropdownBtn.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
            });
          }
        }

        /**
         * CRITICAL INITIAL STATE SETUP (Eliminates All Ghosting)
         * Explicitly set Origin 0 to visible, and origins 1..N to autoAlpha: 0 + visibility: hidden
         */
        if (bgLayers[0]) gsap.set(bgLayers[0], { autoAlpha: 1, visibility: 'visible' });
        if (wordmarks[0]) gsap.set(wordmarks[0], { autoAlpha: 0.90, yPercent: 0, scale: 1, visibility: 'visible' });
        if (artworks[0]) gsap.set(artworks[0], { autoAlpha: 0.45, rotation: 0, scale: 1, visibility: 'visible' });
        if (products[0]) gsap.set(products[0], { autoAlpha: 1, yPercent: 0, scale: 1, rotation: 0, visibility: 'visible' });
        if (metaItems[0]) gsap.set(metaItems[0], { autoAlpha: 1, y: 0, visibility: 'visible', pointerEvents: 'auto' });

        for (let k = 1; k < originCount; k++) {
          if (bgLayers[k]) gsap.set(bgLayers[k], { autoAlpha: 0, visibility: 'hidden' });
          if (wordmarks[k]) gsap.set(wordmarks[k], { autoAlpha: 0, yPercent: 25, visibility: 'hidden' });
          if (artworks[k]) gsap.set(artworks[k], { autoAlpha: 0, rotation: -15, visibility: 'hidden' });
          if (products[k]) gsap.set(products[k], { autoAlpha: 0, yPercent: 32, scale: 0.92, visibility: 'hidden' });
          if (metaItems[k]) gsap.set(metaItems[k], { autoAlpha: 0, y: 20, visibility: 'hidden', pointerEvents: 'none' });
        }

        /**
         * Update UI & Navigation Theme Contrast
         */
        function updateActiveState(index) {
          if (counterCurrent) {
            counterCurrent.textContent = '0' + (index + 1);
          }

          // Boundary state for buttons
          if (prevBtn) {
            if (index === 0) {
              prevBtn.classList.add('is-disabled');
              prevBtn.setAttribute('aria-disabled', 'true');
            } else {
              prevBtn.classList.remove('is-disabled');
              prevBtn.removeAttribute('aria-disabled');
            }
          }

          if (nextBtn) {
            if (index === originCount - 1) {
              nextBtn.classList.add('is-disabled');
              nextBtn.setAttribute('aria-disabled', 'true');
            } else {
              nextBtn.classList.remove('is-disabled');
              nextBtn.removeAttribute('aria-disabled');
            }
          }

          // Navigation contrast theme (e.g., Brazil is dark text)
          const currentBg = bgLayers[index];
          const isDarkTheme = currentBg && currentBg.dataset.navTheme === 'dark';
          if (sectionEl) {
            if (isDarkTheme) {
              sectionEl.classList.add('tfb-nav--dark');
            } else {
              sectionEl.classList.remove('tfb-nav--dark');
            }
          }
        }

        updateActiveState(0);

        if (originCount <= 1) {
          sectionEl._tfbInitialized = true;
          return;
        }

        /**
         * Single Master Scroll Scrubbed Timeline
         * STRICT RULE: Outgoing elements must reach autoAlpha: 0 BEFORE incoming elements begin to enter.
         * Zero simultaneous wordmarks, zero overlapping packshots, zero duplicate CTA buttons.
         */
        const masterTL = gsap.timeline({
          defaults: { ease: 'none' }
        });

        // Loop through each origin transition
        for (let i = 0; i < originCount - 1; i++) {
          const nextIdx = i + 1;
          const label = 'origin_' + i + '_to_' + nextIdx;
          masterTL.addLabel(label);

          // 1. Background transition (smooth continuous cross-fade across 1.0s)
          if (bgLayers[i] && bgLayers[nextIdx]) {
            masterTL.to(bgLayers[i], { autoAlpha: 0, duration: 1.0 }, label);
            masterTL.fromTo(bgLayers[nextIdx],
              { autoAlpha: 0 },
              { autoAlpha: 1, duration: 1.0, immediateRender: false },
              label
            );
          }

          // 2. Product Packshot: Outgoing lifts & fades (0.0 to 0.45); incoming rises (0.45 to 1.0)
          if (products[i] && products[nextIdx]) {
            masterTL.to(products[i], {
              yPercent: -16,
              scale: 0.95,
              rotation: -1.5,
              autoAlpha: 0,
              duration: 0.45,
              ease: 'power1.in'
            }, label);

            masterTL.fromTo(products[nextIdx],
              { yPercent: 16, scale: 0.95, rotation: 1.5, autoAlpha: 0 },
              { yPercent: 0, scale: 1.0, rotation: 0, autoAlpha: 1, duration: 0.55, ease: 'power1.out', immediateRender: false },
              label + '+=0.45'
            );
          }

          // 3. Giant Typography: Outgoing exits cleanly (0.0 to 0.38) BEFORE incoming enters (0.45 to 0.95)
          // GUARANTEE: Outgoing and incoming words NEVER exist simultaneously on screen!
          if (wordmarks[i] && wordmarks[nextIdx]) {
            masterTL.to(wordmarks[i], {
              yPercent: -14,
              scale: 1.02,
              autoAlpha: 0,
              duration: 0.38,
              ease: 'power2.in'
            }, label);

            masterTL.fromTo(wordmarks[nextIdx],
              { yPercent: 14, scale: 0.98, autoAlpha: 0 },
              { yPercent: 0, scale: 1.0, autoAlpha: 0.90, duration: 0.50, ease: 'power2.out', immediateRender: false },
              label + '+=0.45'
            );
          }

          // 4. Botanical Artwork: Outgoing fades out (0.0 to 0.40); incoming enters (0.45 to 0.95)
          if (artworks[i] && artworks[nextIdx]) {
            masterTL.to(artworks[i], {
              rotation: 12,
              scale: 0.90,
              autoAlpha: 0,
              duration: 0.40,
              ease: 'power1.in'
            }, label);

            masterTL.fromTo(artworks[nextIdx],
              { rotation: -12, scale: 0.90, autoAlpha: 0 },
              { rotation: 0, scale: 1.0, autoAlpha: 0.45, duration: 0.50, ease: 'power1.out', immediateRender: false },
              label + '+=0.45'
            );
          }

          // 5. Editorial Card Text: Outgoing drops/exits (0.0 to 0.35); incoming enters cleanly (0.45 to 0.90)
          if (metaItems[i] && metaItems[nextIdx]) {
            masterTL.to(metaItems[i], {
              y: -15,
              autoAlpha: 0,
              duration: 0.35,
              ease: 'power2.in'
            }, label);

            masterTL.fromTo(metaItems[nextIdx],
              { y: 15, autoAlpha: 0 },
              { y: 0, autoAlpha: 1, duration: 0.45, ease: 'power2.out', immediateRender: false },
              label + '+=0.45'
            );
          }
        }

        // Viewport ScrollTrigger Pin
        const scrollDistance = (originCount - 1) * window.innerHeight * 1.2;
        let isNavigating = false;

        const st = ScrollTrigger.create({
          id: 'tfb-hero-' + sectionEl.dataset.sectionId,
          trigger: sectionEl,
          start: 'top top',
          end: function () {
            return '+=' + scrollDistance;
          },
          pin: viewport,
          scrub: 0.5,
          anticipatePin: 1,
          animation: masterTL,
          snap: enableSnap ? {
            snapTo: function (value) {
              if (isNavigating) return value;
              const step = 1 / (originCount - 1);
              return Math.round(value / step) * step;
            },
            duration: 0.4,
            delay: 0.15,
            ease: 'power1.out',
            inertia: false
          } : false,
          onUpdate: function (self) {
            const rawIdx = self.progress * (originCount - 1);
            const nearestIdx = Math.min(originCount - 1, Math.max(0, Math.round(rawIdx)));
            updateActiveState(nearestIdx);
          }
        });

        sectionEl._tfbTimeline = masterTL;
        sectionEl._tfbScrollTrigger = st;

        /**
         * Unified Programmatic Navigation (1 slide per click)
         */
        function goToOrigin(targetIdx) {
          if (isNavigating) return;
          const clampedIdx = Math.max(0, Math.min(targetIdx, originCount - 1));
          const progress = clampedIdx / (originCount - 1);
          const targetY = st.start + (st.end - st.start) * progress;

          isNavigating = true;

          if (window.gsap) {
            gsap.to(window, {
              scrollTo: { y: targetY, autoKill: false },
              duration: 0.5,
              ease: 'power2.out',
              onUpdate: function () {
                st.update();
              },
              onComplete: function () {
                isNavigating = false;
                updateActiveState(clampedIdx);
              }
            });
          } else {
            window.scrollTo({ top: targetY, behavior: 'smooth' });
            setTimeout(function () {
              isNavigating = false;
              updateActiveState(clampedIdx);
            }, 500);
          }
        }

        function handlePrev(e) {
          if (e) {
            e.preventDefault();
            e.stopPropagation();
          }
          if (isNavigating) return;
          const current = Math.round(st.progress * (originCount - 1));
          if (current > 0) goToOrigin(current - 1);
        }

        function handleNext(e) {
          if (e) {
            e.preventDefault();
            e.stopPropagation();
          }
          if (isNavigating) return;
          const current = Math.round(st.progress * (originCount - 1));
          if (current < originCount - 1) goToOrigin(current + 1);
        }

        if (prevBtn) {
          prevBtn.addEventListener('click', handlePrev);
        }

        if (nextBtn) {
          nextBtn.addEventListener('click', handleNext);
        }

        // Keyboard Support
        function handleKeyDown(e) {
          if (!st.isActive) return;
          if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
            const current = Math.round(st.progress * (originCount - 1));
            if (current < originCount - 1) {
              e.preventDefault();
              goToOrigin(current + 1);
            }
          } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
            const current = Math.round(st.progress * (originCount - 1));
            if (current > 0) {
              e.preventDefault();
              goToOrigin(current - 1);
            }
          }
        }

        window.addEventListener('keydown', handleKeyDown);
        sectionEl._tfbKeyHandler = handleKeyDown;

        sectionEl._goToOrigin = goToOrigin;
        sectionEl._tfbInitialized = true;
      })
      .catch(function (err) {
        console.warn('The Flying Beans Hero: initialization deferred.', err);
      });
  }

  /**
   * Scoped Cleanup
   */
  function destroyOriginHero(sectionEl) {
    if (!sectionEl) return;

    if (sectionEl._tfbKeyHandler) {
      window.removeEventListener('keydown', sectionEl._tfbKeyHandler);
      sectionEl._tfbKeyHandler = null;
    }
    if (sectionEl._tfbTimeline) {
      sectionEl._tfbTimeline.kill();
      sectionEl._tfbTimeline = null;
    }
    if (sectionEl._tfbScrollTrigger) {
      sectionEl._tfbScrollTrigger.kill(true);
      sectionEl._tfbScrollTrigger = null;
    }
    sectionEl._tfbInitialized = false;
  }

  function initAll() {
    const heroes = document.querySelectorAll('.tfb-origin-hero');
    heroes.forEach(function (hero) {
      initOriginHero(hero);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Shopify Theme Editor Hooks
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', function (event) {
      const hero = event.target.querySelector('.tfb-origin-hero') ||
        (event.target.classList.contains('tfb-origin-hero') ? event.target : null);
      if (hero) initOriginHero(hero);
    });

    document.addEventListener('shopify:section:unload', function (event) {
      const hero = event.target.querySelector('.tfb-origin-hero') ||
        (event.target.classList.contains('tfb-origin-hero') ? event.target : null);
      if (hero) destroyOriginHero(hero);
    });

    document.addEventListener('shopify:block:select', function (event) {
      const hero = event.target.closest('.tfb-origin-hero');
      if (hero && hero._goToOrigin) {
        const index = parseInt(event.target.dataset.index, 10);
        if (!isNaN(index)) hero._goToOrigin(index);
      }
    });
  }
})();
